import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Facebook, Mail, Upload, CheckCircle2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { PRODUCT_OPTIONS, PRICE_MAP, SAME_DAY_FEE } from "@/data/content";
import { waLink, messengerLink, mailtoLink, SITE } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-[border-color,box-shadow] duration-300";

export const OrderForm = ({ preselect }) => {
  const match = preselect && PRODUCT_OPTIONS.find((o) => o.startsWith(preselect));
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    product_size: match || PRODUCT_OPTIONS[0],
    quantity: 1, delivery_method: "Delivery", same_day: false, notes: "",
  });
  const [file, setFile] = useState(null);
  const [paymentFile, setPaymentFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const unitPrice = PRICE_MAP[form.product_size] || 0;
  const total = unitPrice * Number(form.quantity || 1) + (form.same_day && form.delivery_method === "Delivery" ? SAME_DAY_FEE : 0);
  const advance = total / 2;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const summary = `New MetalMuse order:%0A`.replace("%0A", "\n") +
    `Name: ${form.name}\nProduct: ${form.product_size}\nQuantity: ${form.quantity}\n` +
    `Delivery/Pickup: ${form.delivery_method}${form.same_day ? " (same-day +$5)" : ""}\n` +
    (form.notes ? `Notes: ${form.notes}\n` : "") +
    `Payment: 50% advance $${advance.toFixed(2)} CAD sent via Interac e-Transfer (order total $${total.toFixed(2)} CAD)\n` +
    (paymentFile ? `(Payment screenshot attached on the website order form)\n` : "") +
    (file ? `(Photo attached on the website order form)\n` : "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paymentFile) {
      toast.error("Please attach your Interac payment screenshot (50% advance).");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("payment_method", "interac");
      fd.append("payment_total", total.toFixed(2));
      fd.append("payment_advance", advance.toFixed(2));
      if (file) fd.append("photo", file);
      if (paymentFile) fd.append("payment_screenshot", paymentFile);
      await axios.post(`${API}/orders`, fd);
      setSubmitted(true);
      toast.success("Order request received!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="order-success" className="bg-[#121212] border border-[#00E5FF]/30 rounded-2xl p-10 text-center cyan-glow">
        <CheckCircle2 size={48} className="text-[#00E5FF] mx-auto mb-6" />
        <h3 className="font-tech text-2xl md:text-3xl text-[#F5F5F7]">Order request received</h3>
        <p className="text-[#A1A1A6] mt-4 leading-relaxed max-w-md mx-auto">
          Thank you, {form.name.split(" ")[0]}. To confirm your order fastest, send it to us on your favourite channel:
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <a href={waLink(summary)} target="_blank" rel="noopener noreferrer" data-testid="order-success-whatsapp" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3.5 rounded-full hover:brightness-110 transition-[filter,transform] duration-300 hover:-translate-y-0.5">
            <MessageCircle size={18} /> WhatsApp
          </a>
          <a href={messengerLink} target="_blank" rel="noopener noreferrer" data-testid="order-success-messenger" className="inline-flex items-center justify-center gap-2 bg-[#0084FF] text-white font-semibold px-6 py-3.5 rounded-full hover:brightness-110 transition-[filter,transform] duration-300 hover:-translate-y-0.5">
            <Facebook size={18} /> Messenger
          </a>
          <a href={mailtoLink("New Metal Print Order", summary)} data-testid="order-success-email" className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-medium px-6 py-3.5 rounded-full hover:bg-white/5 transition-colors duration-300">
            <Mail size={18} /> Email
          </a>
        </div>
        <button onClick={() => setSubmitted(false)} data-testid="order-new-button" className="mt-8 text-sm text-[#A1A1A6] hover:text-[#00E5FF] transition-colors duration-300 underline underline-offset-4">
          Place another order
        </button>
        <p className="text-xs text-[#A1A1A6]/70 mt-6 max-w-md mx-auto">
          Your 50% Interac advance will be verified before printing begins. The remaining balance is due on delivery or pickup.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="order-form" className="bg-[#121212] border border-white/5 rounded-2xl p-8 md:p-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="order-name" className="text-sm text-[#A1A1A6] mb-2 block">Name *</Label>
          <input id="order-name" data-testid="order-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" className={inputCls} />
        </div>
        <div>
          <Label htmlFor="order-email" className="text-sm text-[#A1A1A6] mb-2 block">Email *</Label>
          <input id="order-email" data-testid="order-email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className={inputCls} />
        </div>
        <div>
          <Label htmlFor="order-phone" className="text-sm text-[#A1A1A6] mb-2 block">Phone *</Label>
          <input id="order-phone" data-testid="order-phone-input" type="tel" required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(306) 555-0123" className={inputCls} />
        </div>
        <div>
          <Label className="text-sm text-[#A1A1A6] mb-2 block">Product Size *</Label>
          <Select value={form.product_size} onValueChange={(v) => set("product_size", v)}>
            <SelectTrigger data-testid="order-product-select" className="bg-[#1A1A1A] border-white/10 rounded-xl px-4 py-3 h-auto text-white focus:ring-[#00E5FF]">
              <SelectValue placeholder="Choose a product" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
              {PRODUCT_OPTIONS.map((p) => (
                <SelectItem key={p} value={p} data-testid={`order-product-option-${p.slice(0, 5).replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} className="focus:bg-[#00E5FF]/20 focus:text-white">
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="order-quantity" className="text-sm text-[#A1A1A6] mb-2 block">Quantity *</Label>
          <input id="order-quantity" data-testid="order-quantity-input" type="number" min="1" max="99" required value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label className="text-sm text-[#A1A1A6] mb-2 block">Delivery or Pickup *</Label>
          <RadioGroup value={form.delivery_method} onValueChange={(v) => set("delivery_method", v)} className="flex gap-4 pt-2" data-testid="order-delivery-radio">
            {["Delivery", "Pickup"].map((m) => (
              <div key={m} className="flex items-center gap-2">
                <RadioGroupItem value={m} id={`delivery-${m}`} data-testid={`order-delivery-${m.toLowerCase()}`} className="border-white/30 text-[#00E5FF]" />
                <Label htmlFor={`delivery-${m}`} className="text-[#F5F5F7] cursor-pointer">{m}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </div>

      {form.delivery_method === "Delivery" && (
        <div className="flex items-center gap-3 mt-6 bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-3">
          <Checkbox id="same-day" data-testid="order-same-day-checkbox" checked={form.same_day} onCheckedChange={(v) => set("same_day", !!v)} className="border-white/30 data-[state=checked]:bg-[#00E5FF] data-[state=checked]:border-[#00E5FF] data-[state=checked]:text-black" />
          <Label htmlFor="same-day" className="text-sm text-[#F5F5F7] cursor-pointer">
            Same-day delivery <span className="text-[#00E5FF] font-semibold">+$5</span> <span className="text-[#A1A1A6]">(qualifying orders)</span>
          </Label>
        </div>
      )}

      <div className="mt-6">
        <Label htmlFor="order-photo" className="text-sm text-[#A1A1A6] mb-2 block">Upload Photo</Label>
        <label
          htmlFor="order-photo"
          data-testid="order-photo-dropzone"
          className="flex flex-col items-center justify-center gap-3 border border-dashed border-white/20 rounded-xl px-4 py-10 cursor-pointer hover:border-[#00E5FF]/60 hover:bg-white/[0.02] transition-[border-color,background-color] duration-300"
        >
          <Upload size={26} className="text-[#00E5FF]" />
          <span className="text-sm text-[#A1A1A6]">
            {file ? <span className="text-[#F5F5F7] font-medium">{file.name}</span> : "Tap to upload your photo (JPG, PNG, WEBP)"}
          </span>
          <input id="order-photo" data-testid="order-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
      </div>

      <div className="mt-6">
        <Label htmlFor="order-notes" className="text-sm text-[#A1A1A6] mb-2 block">Additional Notes</Label>
        <textarea id="order-notes" data-testid="order-notes-input" rows="4" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything we should know — occasion, deadline, framing ideas..." className={`${inputCls} resize-none`} />
      </div>

      <div className="mt-8 bg-[#0A0A0A] border border-[#00E5FF]/25 rounded-2xl p-6 md:p-8" data-testid="payment-panel">
        <p className="uppercase tracking-[0.25em] text-xs font-semibold text-[#00E5FF] mb-5">Payment — 50% Advance via Interac e-Transfer</p>
        <div className="flex flex-wrap gap-x-10 gap-y-2 text-sm mb-6">
          <p className="text-[#A1A1A6]">Order total: <span data-testid="payment-total" className="text-[#F5F5F7] font-semibold">${total.toFixed(2)} CAD</span></p>
          <p className="text-[#A1A1A6]">Advance due now: <span data-testid="payment-advance" className="text-[#00E5FF] font-semibold">${advance.toFixed(2)} CAD</span></p>
        </div>
        <ol className="text-sm text-[#A1A1A6] leading-relaxed list-decimal list-inside space-y-2 mb-6">
          <li>Send <span className="text-[#F5F5F7] font-medium">${advance.toFixed(2)} CAD</span> by Interac e-Transfer to <span className="text-[#F5F5F7] font-medium">{SITE.email}</span></li>
          <li>Take a screenshot of your payment confirmation</li>
          <li>Attach the screenshot below — printing begins once your advance is verified</li>
        </ol>
        <label
          htmlFor="order-payment-shot"
          data-testid="order-payment-dropzone"
          className="flex flex-col items-center justify-center gap-3 border border-dashed border-[#00E5FF]/40 rounded-xl px-4 py-8 cursor-pointer hover:border-[#00E5FF] hover:bg-[#00E5FF]/5 transition-[border-color,background-color] duration-300"
        >
          <Upload size={24} className="text-[#00E5FF]" />
          <span className="text-sm text-[#A1A1A6]">
            {paymentFile ? <span className="text-[#F5F5F7] font-medium">{paymentFile.name}</span> : "Attach payment screenshot *"}
          </span>
          <input id="order-payment-shot" data-testid="order-payment-screenshot-input" type="file" accept="image/*" className="hidden" onChange={(e) => setPaymentFile(e.target.files?.[0] || null)} />
        </label>
        <p className="text-xs text-[#A1A1A6]/70 mt-4">The remaining 50% (${advance.toFixed(2)} CAD) is due on delivery or pickup.</p>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="order-submit-button"
        className="mt-8 w-full bg-white text-black font-tech text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 rounded-full hover:bg-cyan-200 transition-[background-color,box-shadow,transform] duration-300 shadow-[0_0_20px_rgba(0,229,255,0.3)] hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {loading ? "Sending..." : "Order Your Metal Print"}
      </button>
        <p className="text-xs text-[#A1A1A6]/70 text-center mt-4">
          Free photo enhancement included. Your 50% Interac advance is verified before printing begins — the balance is due on delivery or pickup.
        </p>
    </form>
  );
};
